// Lightweight JSON viewer — pretty-printed, token-colored (via React nodes, not
// dangerouslySetInnerHTML), copy-to-clipboard, collapsible. No external deps.

import React, { useMemo, useState } from 'react';

const TOKEN = /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g;

function highlight(json: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(json)) !== null) {
    if (m.index > last) {
nodes.push(json.slice(last, m.index));
}
    const tok = m[0];
    let cls = 'text-emerald-300'; // number
    if (tok.startsWith('"')) {
      cls = tok.trimEnd().endsWith(':') ? 'text-sky-300' : 'text-amber-200'; // key vs string
    } else if (tok === 'true' || tok === 'false') {
      cls = 'text-fuchsia-300';
    } else if (tok === 'null') {
      cls = 'text-zinc-500';
    }
    nodes.push(
      <span key={key++} className={cls}>{tok}</span>
    );
    last = TOKEN.lastIndex;
  }
  if (last < json.length) {
nodes.push(json.slice(last));
}
  return nodes;
}

export function JsonView({
  value,
  maxHeight = '22rem',
  empty = 'No data'
}: {
  value: unknown;
  maxHeight?: string;
  empty?: string;
}) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => {
    if (value === null || value === undefined) {
return '';
}
    try {
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard blocked */ }
  };

  if (!text) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-6 text-center text-xs text-zinc-500">
        {empty}
      </div>
    );
  }

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 z-10 rounded-md border border-zinc-700 bg-zinc-800/80 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-zinc-300 opacity-0 transition group-hover:opacity-100 hover:border-zinc-500 hover:text-white"
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
      <pre
        className="overflow-auto rounded-lg border border-zinc-800 bg-zinc-950 p-4 font-mono text-xs leading-relaxed text-zinc-200 shadow-inner"
        style={{ maxHeight }}
      >
        <code>{highlight(text)}</code>
      </pre>
    </div>
  );
}
